import { useTranslation } from 'react-i18next';
import type { JSX } from 'react';

import { cn } from '@/lib/utils';

import type {
  BackgroundMode,
  BackgroundQualitySetting,
  ResolvedBackgroundQuality,
} from '../media/backgroundProcessor';
import { VIDEO_FILTERS } from '../media/videoEffects';
import type { BlurStrength, VideoFilter } from '../media/videoEffects';
import { FACE_MASKS } from '../media/faceMasks';
import type { FaceMaskId } from '../media/faceMasks';

/**
 * The camera-effect pickers shared by the prejoin preview overlay and the effects dialog:
 * one source for the option lists, labels and active-chip styling, with the testid prefixes
 * as parameters (the prejoin pins `bg-mode-*` / `bg-quality-*` / `blur-strength-*` in e2e;
 * the dialog uses `effects-bg-mode-*` / `effects-bg-quality-*` / `effects-blur-strength-*`
 * and `filter-*` for the color filter row).
 */

/** Background treatments: real segmentation-based blur/replacement plus the raw camera. */
const BACKGROUND_MODES: readonly { id: BackgroundMode; labelKey: string }[] = [
  { id: 'none', labelKey: 'prejoin.bg_none' },
  { id: 'blur', labelKey: 'prejoin.bg_blur' },
  { id: 'office', labelKey: 'prejoin.bg_office' },
];

/**
 * Background quality levels: "auto" benchmarks the machine once at processor
 * start (the resolved level shows next to the label), "low" targets weak
 * machines, "high" powerful ones. Visible only while a background effect is on;
 * switching the level restarts the processor with the new parameters.
 */
const BACKGROUND_QUALITIES: readonly { id: BackgroundQualitySetting; labelKey: string }[] = [
  { id: 'auto', labelKey: 'prejoin.bg_quality_auto' },
  { id: 'low', labelKey: 'prejoin.bg_quality_low' },
  { id: 'high', labelKey: 'prejoin.bg_quality_high' },
];

/** Blur strength presets: multipliers of the quality level's base blur radius. */
const BLUR_STRENGTH_OPTIONS: readonly { id: BlurStrength; labelKey: string }[] = [
  { id: 'light', labelKey: 'prejoin.bg_strength_light' },
  { id: 'medium', labelKey: 'prejoin.bg_strength_medium' },
  { id: 'strong', labelKey: 'prejoin.bg_strength_strong' },
];

/** Whole-frame color filter presets (the «Фильтры» row in the effects dialog). */
const VIDEO_FILTER_OPTIONS: readonly { id: VideoFilter; labelKey: string }[] = VIDEO_FILTERS.map(
  (id) => ({ id, labelKey: `effects.filter_${id}` }),
);

/** Face-mask presets (the «Маски» row in the effects dialog). */
const FACE_MASK_OPTIONS: readonly { id: FaceMaskId; labelKey: string }[] = FACE_MASKS.map((id) => ({
  id,
  labelKey: `effects.mask_${id}`,
}));

const CHIP_ROW_CLASS = 'flex items-center gap-1.5 rounded-lg bg-black/55 p-1 backdrop-blur-sm';

function chipClass(active: boolean): string {
  return cn(
    'rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors',
    active ? 'bg-white text-zinc-950' : 'text-zinc-300 hover:bg-white/10',
  );
}

/** The none/blur/office treatment chips; the active mode renders as a solid white chip. */
export function BackgroundModePicker(props: {
  readonly mode: BackgroundMode;
  readonly onMode: (mode: BackgroundMode) => void;
  readonly testidPrefix: string;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div title={t('prejoin.bg_preview_note')} className={CHIP_ROW_CLASS}>
      {BACKGROUND_MODES.map((background) => {
        const active = background.id === props.mode;
        return (
          <button
            key={background.id}
            type="button"
            data-testid={`${props.testidPrefix}-${background.id}`}
            title={t(background.labelKey)}
            onClick={() => {
              props.onMode(background.id);
            }}
            className={chipClass(active)}
          >
            {t(background.labelKey)}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The auto/low/high quality chips; "auto" shows what the benchmark resolved to once it
 * settles (e.g. "Auto · Max"). Rendered by the caller only while a treatment is active.
 */
export function BackgroundQualityPicker(props: {
  readonly quality: BackgroundQualitySetting;
  readonly onQuality: (quality: BackgroundQualitySetting) => void;
  readonly resolvedQuality: ResolvedBackgroundQuality | null;
  readonly testidPrefix: string;
  readonly wrapperTestid: string;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      data-testid={props.wrapperTestid}
      title={t('prejoin.bg_quality_note')}
      className={CHIP_ROW_CLASS}
    >
      {BACKGROUND_QUALITIES.map((option) => {
        const active = option.id === props.quality;
        const label =
          option.id === 'auto' && props.resolvedQuality !== null
            ? `${t('prejoin.bg_quality_auto')} · ${t(
                props.resolvedQuality === 'high'
                  ? 'prejoin.bg_quality_high'
                  : 'prejoin.bg_quality_low',
              )}`
            : t(option.labelKey);
        return (
          <button
            key={option.id}
            type="button"
            data-testid={`${props.testidPrefix}-${option.id}`}
            title={label}
            onClick={() => {
              props.onQuality(option.id);
            }}
            className={chipClass(active)}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The blur strength chips («Слабое / Среднее / Сильное»), styled like the other rows;
 * rendered by the caller only while the blur treatment is active - the strength has no
 * effect on the office replacement or the raw camera.
 */
export function BlurStrengthPicker(props: {
  readonly strength: BlurStrength;
  readonly onStrength: (strength: BlurStrength) => void;
  readonly testidPrefix: string;
  readonly wrapperTestid: string;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      data-testid={props.wrapperTestid}
      title={t('prejoin.bg_strength_note')}
      className={CHIP_ROW_CLASS}
    >
      {BLUR_STRENGTH_OPTIONS.map((option) => {
        const active = option.id === props.strength;
        const label = t(option.labelKey);
        return (
          <button
            key={option.id}
            type="button"
            data-testid={`${props.testidPrefix}-${option.id}`}
            title={label}
            onClick={() => {
              props.onStrength(option.id);
            }}
            className={chipClass(active)}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The whole-frame color filter chips («Нет / Яркий / ...»), each with a small swatch dot
 * previewing the tint; the active filter renders as a solid white chip. Visible on every
 * background treatment - a filter applies to the whole frame in all of them.
 */
export function VideoFilterPicker(props: {
  readonly filter: VideoFilter;
  readonly onFilter: (filter: VideoFilter) => void;
  readonly testidPrefix: string;
  readonly wrapperTestid: string;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      data-testid={props.wrapperTestid}
      title={t('effects.filters_note')}
      className={CHIP_ROW_CLASS}
    >
      {VIDEO_FILTER_OPTIONS.map((option) => {
        const active = option.id === props.filter;
        const label = t(option.labelKey);
        return (
          <button
            key={option.id}
            type="button"
            data-testid={`${props.testidPrefix}-${option.id}`}
            title={label}
            onClick={() => {
              props.onFilter(option.id);
            }}
            className={chipClass(active)}
          >
            <span
              aria-hidden
              className={cn(
                'mr-1 inline-block size-2 rounded-full align-[-1px]',
                FILTER_SWATCH[option.id],
              )}
            />
            {label}
          </button>
        );
      })}
    </div>
  );
}

/** Swatch dot background per filter preset (a hint of the tint at a glance). */
const FILTER_SWATCH: Readonly<Record<VideoFilter, string>> = {
  none: 'bg-zinc-600',
  bright: 'bg-amber-300',
  warm: 'bg-orange-400',
  cool: 'bg-sky-400',
  mono: 'bg-zinc-300',
  sepia: 'bg-yellow-700',
};

/**
 * The face-mask chips («Нет / Очки / Усы / Шляпа / Корона»), styled like the other
 * rows; each carries a tiny neutral mask hint glyph (a dot in the mask's own
 * palette color). Rendered by the effects dialog only - the choice applies live
 * on every background treatment and persists for the next join.
 */
export function FaceMaskPicker(props: {
  readonly mask: FaceMaskId;
  readonly onMask: (mask: FaceMaskId) => void;
  readonly testidPrefix: string;
  readonly wrapperTestid: string;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      data-testid={props.wrapperTestid}
      title={t('effects.masks_note')}
      className={CHIP_ROW_CLASS}
    >
      {FACE_MASK_OPTIONS.map((option) => {
        const active = option.id === props.mask;
        const label = t(option.labelKey);
        return (
          <button
            key={option.id}
            type="button"
            data-testid={`${props.testidPrefix}-${option.id}`}
            title={label}
            onClick={() => {
              props.onMask(option.id);
            }}
            className={chipClass(active)}
          >
            <span
              aria-hidden
              className={cn(
                'mr-1 inline-block size-2 rounded-full align-[-1px]',
                MASK_SWATCH[option.id],
              )}
            />
            {label}
          </button>
        );
      })}
    </div>
  );
}

/** Swatch dot background per mask preset (a hint of the mask's palette at a glance). */
const MASK_SWATCH: Readonly<Record<FaceMaskId, string>> = {
  none: 'bg-zinc-600',
  glasses: 'bg-zinc-900',
  mustache: 'bg-zinc-700',
  hat: 'bg-zinc-800',
  crown: 'bg-emerald-400',
};
